<template>
  <v-container fluid class="py-8">
    <!-- Header -->
    <v-row class="mb-4" align="center" justify="space-between">
      <v-col cols="auto">
        <span class="text-overline text-medium-emphasis d-block mb-1">Notifications</span>
        <h1 class="text-h5 font-weight-bold">Your Notifications</h1>
      </v-col>
      <v-col cols="auto" class="d-flex ga-2">
        <v-switch
          v-model="unreadOnly"
          color="primary"
          hide-details
          label="Unread only"
          @change="applyFilters"
        />
        <v-chip-group v-model="typeFilter" mandatory class="ml-2">
          <v-chip value="all" color="grey">All</v-chip>
          <v-chip value="system" color="blue">System</v-chip>
          <v-chip value="listing" color="green">Listing</v-chip>
        </v-chip-group>
        <v-btn
          variant="outlined"
          color="secondary"
          class="ml-2"
          :disabled="isLoading || (items || []).length === 0"
          @click="markAllRead"
        >
          Mark all read
        </v-btn>
      </v-col>
    </v-row>

    <!-- Loading skeleton -->
    <v-card v-if="isLoading" elevation="2" class="mb-6">
      <v-card-title>Notifications</v-card-title>
      <v-card-text>
        <!-- header skeleton -->
        <div class="d-flex mb-2">
          <v-skeleton-loader type="text" width="12%" class="mr-4" />
          <v-skeleton-loader type="text" width="30%" class="mr-4" />
          <v-skeleton-loader type="text" width="20%" class="mr-4" />
          <v-skeleton-loader type="text" width="10%" class="mr-4" />
          <v-skeleton-loader type="text" width="8%" class="ml-auto" />
        </div>
        <!-- rows skeleton -->
        <div v-for="n in pageSize" :key="n" class="d-flex align-center py-3">
          <v-skeleton-loader type="text" width="12%" class="mr-4" />
          <v-skeleton-loader type="text" width="30%" class="mr-4" />
          <v-skeleton-loader type="text" width="20%" class="mr-4" />
          <v-skeleton-loader type="text" width="10%" class="mr-4" />
          <v-skeleton-loader type="button" width="140" class="ml-auto" />
        </div>
      </v-card-text>
    </v-card>

    <!-- Real table -->
    <v-card v-else elevation="2" class="mb-6">
      <v-card-title>Notifications</v-card-title>
      <v-card-text>
        <v-table>
          <thead>
            <tr>
              <th class="text-left">Type</th>
              <th class="text-left">Title</th>
              <th class="text-left">Created</th>
              <th class="text-left">Status</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in items" :key="item.id">
              <td>
                <v-chip size="small" variant="flat" :color="typeColor(item.listing_id)" class="text-capitalize">
                  {{ item.listing_id ? 'Listing' : 'System' }}
                </v-chip>
              </td>

              <td>
                <div class="font-weight-medium">{{ item.title }}</div>
                <div v-if="item.body" class="text-medium-emphasis small text-truncate">
                  {{ item.body }}
                </div>
              </td>

              <td>{{ formatDateTime(item.created_at) }}</td>

              <td>
                <v-chip size="x-small" :color="item.read_at ? 'grey' : 'primary'" variant="tonal">
                  {{ item.read_at ? 'Read' : 'Unread' }}
                </v-chip>
              </td>

              <td class="text-right">
                <v-btn
                  size="small"
                  variant="outlined"
                  color="secondary"
                  class="mr-2"
                  @click="open(item)"
                >
                  Open
                </v-btn>
                <v-btn
                  size="small"
                  variant="text"
                  color="primary"
                  :disabled="!!item.read_at"
                  @click="markRead(item)"
                >
                  Mark read
                </v-btn>
              </td>
            </tr>

            <tr v-if="(items || []).length === 0">
              <td colspan="5" class="text-center text-medium-emphasis py-6">
                No notifications found.
              </td>
            </tr>
          </tbody>
        </v-table>

        <!-- Pagination -->
        <v-row class="mt-4" align="center" justify="space-between">
          <v-col cols="auto">
            <small class="text-medium-emphasis">
              Showing {{ pageStart + 1 }}–{{ pageEnd }} of {{ totalCount }}
            </small>
          </v-col>
          <v-col cols="auto">
            <v-pagination
              v-model="currentPage"
              :length="totalPages"
              :total-visible="5"
              prev-icon="mdi-chevron-left"
              next-icon="mdi-chevron-right"
              @update:modelValue="goToPage"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" location="bottom right" timeout="2400">
      {{ snackbar.msg }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { supabase } from '@/supabaseClient'

export default {
  name: 'NotificationsTableVuetify',
  data() {
    return {
      userId: null,

      items: [],
      pageSize: 10,
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      isLoading: true,

      // simple filters (no v-slots)
      typeFilter: 'all',   // 'all' | 'system' | 'listing'
      unreadOnly: false,

      snackbar: { show: false, color: 'error', msg: '' },
    }
  },
  computed: {
    pageStart() { return (this.currentPage - 1) * this.pageSize },
    pageEnd() { return Math.min(this.pageStart + this.pageSize, this.totalCount || 0) },
  },
  methods: {
    typeColor(t) {
      return t ? 'green' : 'blue'
    },
    formatDateTime(d) {
      if (!d) return '—'
      const dt = new Date(d)
      return isNaN(dt) ? '—' : dt.toLocaleString()
    },
    toast(msg, color = 'error') { this.snackbar = { show: true, color, msg } },

    async fetchNotifications(page = 1) {
      if (!this.userId) return
      this.isLoading = true
      const from = (page - 1) * this.pageSize
      const to = from + this.pageSize - 1
      try {
        let query = supabase
          .from('notifications')
          .select('*', { count: 'exact' })
          .eq('user_id', this.userId)

        if (this.typeFilter !== 'all') query = query.eq('type', this.typeFilter)
        if (this.unreadOnly) query = query.is('read_at', null)

        query = query.order('created_at', { ascending: false }).range(from, to)

        const { data, count, error } = await query
        if (error) throw error

        this.items = data || []
        this.totalCount = count ?? this.items.length
        this.totalPages = Math.max(1, Math.ceil((this.totalCount || 0) / this.pageSize))
      } catch (err) {
        console.error('Error fetching notifications:', err)
        this.items = []
        this.totalCount = 0
        this.totalPages = 1
        this.toast('Failed to load notifications.')
      } finally {
        this.isLoading = false
      }
    },

    goToPage(page) {
      if (page === this.currentPage) return
      this.currentPage = page
      this.fetchNotifications(page)
    },

    applyFilters() {
      this.currentPage = 1
      this.fetchNotifications(1)
    },

    async markRead(item) {
      try {
        const { error } = await supabase
          .from('notifications')
          .update({ read_at: new Date().toISOString() })
          .eq('id', item.id)
        if (error) throw error
        this.toast('Marked as read', 'success')
        this.fetchNotifications(this.currentPage)
      } catch (err) {
        console.error('markRead error:', err)
        this.toast('Could not mark as read.')
      }
    },

    async markAllRead() {
      try {
        let query = supabase
          .from('notifications')
          .update({ read_at: new Date().toISOString() })
          .eq('user_id', this.userId)
          .is('read_at', null)
        if (this.typeFilter !== 'all') query = query.eq('type', this.typeFilter)

        const { error } = await query
        if (error) throw error
        this.toast('All visible notifications marked read', 'success')
        this.fetchNotifications(this.currentPage)
      } catch (err) {
        console.error('markAllRead error:', err)
        this.toast('Could not mark all as read.')
      }
    },

    open(item) {
      if (item.type === 'listing' && item.listing_id) {
        this.$router.push(`/dashboard/listings/admin/${item.listing_id}`)
      } else {
        this.toast(item.title, 'secondary')
      }
    },
  },
  async mounted() {
    const { data } = await supabase.auth.getUser()
    this.userId = data?.user?.id || null
    await this.fetchNotifications(this.currentPage)
  },
}
</script>

<style scoped>
.v-table thead th { font-weight: 600; }
.text-truncate {
  max-width: 520px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.small { font-size: 0.85rem; }
</style>
